import { PaletteColorOptions, createTheme } from "@mui/material";
import { DataGridProProps } from "@mui/x-data-grid-pro";
import { DateRangePickerDayProps } from "@mui/x-date-pickers-pro";
import { Dayjs } from "dayjs";

import {
  ACCORDION_DETAILS_THEME,
  ACCORDION_SUMMARY_THEME,
  ACCORDION_THEME,
} from "../components/Accordion/Accordion.theme";
import {
  ALERT_THEME,
  ALERT_TITLE_THEME,
} from "../components/Alert/Alert.theme";
import BUTTON_THEME from "../components/Button/Button.theme";
import BUTTON_GROUP_THEME from "../components/ButtonGroup/ButtonGroup.theme";
import CHECKBOX_THEME from "../components/Checkbox/Checkbox.theme";
import CHIP_THEME from "../components/Chip/Chip.theme";
import { DATA_GRID_PRO_THEME } from "../components/DataGrid/DataGrid.theme";
import { PICKERS_DAY_THEME } from "../components/DatePicker/DatePicker.theme";
import { DATE_RANGE_PICKER_DAY_THEME } from "../components/DateRangePicker/DateRangePicker.theme";
import { ARROW_SWITCHER_THEME } from "../components/DateTimePicker/DateTimePicker.theme";
import ICON_THEME from "../components/Icon/Icon.theme";
import { ICON_BUTTON_THEME } from "../components/IconButton/IconButton.theme";
import { BaseImage } from "../components/Image/BaseImage";
import { ImageType } from "../components/Image/types";
import { MENU_ITEM_THEME, MENU_THEME } from "../components/Menu/Menu.theme";
import { MODAL_THEME } from "../components/Modal/Modal.theme";
import PAGINATION_THEME from "../components/Pagination/Pagination.theme";
import POPPER_THEME from "../components/Popper/Popper.theme";
import PROGRESS_BAR_THEME from "../components/ProgressBar/ProgressBar.theme";
import { RADIO_THEME } from "../components/Radio/Radio.theme";
import { MuiSkeleton as SKELETON_THEME } from "../components/Skeleton/Skeleton.theme";
import { SLIDER_THEME } from "../components/Slider/Slider.theme";
import {
  SNACKBAR_CONTENT_THEME,
  SNACKBAR_THEME,
} from "../components/Snackbar/Snackbar.theme";
import { STEPPER_THEME } from "../components/Stepper/Stepper.theme";
import { SWITCH_THEME } from "../components/Switch/Switch.theme";
import { TAB_PANEL_THEME, TAB_THEME } from "../components/Tabs/Tabs.theme";
import {
  OUTLINED_INPUT_THEME,
  TEXT_FIELD_THEME,
} from "../components/TextField/TextField.theme";
import { MULTI_SECTION_DIGITAL_CLOCK_SECTION_THEME } from "../components/TimePicker/TimePicker.theme";
import TOOLTIP_THEME from "../components/Tooltip/Tooltip.theme";
import TYPOGRAPHY_THEME, {
  applyAdditionalTypographyTheme,
} from "../components/Typography/Typography.theme";
import pxToRem from "../utils/pxToRem";
import {
  apple,
  bubblegum,
  gray,
  peacock,
  redhot,
  sunshine,
  waterslide,
} from "./colors";
import { shadows } from "./shadows";

/**
 * These imports extend the Theme type to include components from other MUI packages, such as Lab and X.
 */
import type {} from "@mui/lab/themeAugmentation"; // https://mui.com/material-ui/about-the-lab/#typescript
import type {} from "@mui/x-date-pickers/themeAugmentation"; // https://mui.com/x/react-date-pickers/base-concepts/#typescript

declare module "@mui/material/styles" {
  interface TypeText {
    default: string;
    subdue: string;
    invert: string;
    brandPrimary: string;
    brandSecondary: string;
    error: string;
    warning: string;
    info: string;
    success: string;
  }

  interface TypeIcon {
    default: string;
    subdue: string;
    disabled: string;
    invert: string;
    primary: string;
    secondary: string;
    error: string;
    warning: string;
    info: string;
    success: string;
  }

  interface TypeBorder {
    neutralDefault: string;
    neutralLighter: string;
    neutralDarker: string;
    neutralEmphasis: string;
    peacockDefault: string;
    peacockLighter: string;
    peacockDarker: string;
    peacockEmphasis: string;
  }

  interface TypeSurface {
    white: string;
    neutral50: string;
    neutral100: string;
    neutral200: string;
    peacock50: string;
    peacock100: string;
    peacock200: string;
    disabled: string;
  }

  interface Palette {
    neutral: PaletteColor;
    icon: TypeIcon;
    border: TypeBorder;
    surface: TypeSurface;
    overlays: {
      transparentGrayLight: string;
      transparentGrayDark: string;
    };
  }

  // Restrict palette color shades to only the valid ones defined in our color system.
  // This provides type safety so you can't use random numbers like "primary.999".
  // Valid shades: 50, 100, 200, 300, 400, 500, 600, 700, 800, 900
  // Gray also has 0 (white) and 950 (black) but these are accessed via common.white/black
  interface PaletteColor {
    0?: string; // Optional - only gray has this
    50: string;
    100: string;
    200: string;
    300: string;
    400: string;
    500: string;
    600: string;
    700: string;
    800: string;
    900: string;
    950?: string; // Optional - only gray has this
  }

  interface PaletteOptions {
    neutral?: PaletteColorOptions;
    icon?: Partial<TypeIcon>;
    border?: Partial<TypeBorder>;
    surface?: Partial<TypeSurface>;
    overlays?: {
      transparentGrayLight?: string;
      transparentGrayDark?: string;
    };
  }

  interface Colors {
    gray50: string;
    gray200: string;
    gray600: string;
    gray800: string;
    gray900: string;
    peacock50: string;
    peacock500: string;
    peacock700: string;
  }
  interface Gradients {
    primarySecondary?: string;
    rainbow?: string;
  }

  interface BoxShadows {
    none: string;
    50: string;
    100: string;
    200: string;
    400: string;
  }

  interface Theme {
    // Make optional because there's no guarantee that the theme will have gradients, depending on how it's rendered.
    // e.g. in tests it uses a default theme that doesn't contain this property
    gradients?: Gradients;
    colors: Colors;
    boxShadows: BoxShadows;
  }

  interface ThemeOptions {
    gradients?: Gradients;
    boxShadows?: BoxShadows;
  }
}

declare module "@mui/material" {
  /**
   * Custom component theming
   */
  // Image
  interface Components {
    MuiImage?: {
      BaseComponent: ImageType;
    };
    MuiDataGrid?: {
      defaultProps?: DataGridProProps["MuiDataGrid"];
    };
    MuiDateRangePickerDay?: {
      defaultProps?: DateRangePickerDayProps<Dayjs>["MuiDateRangePickerDay"];
    };
  }
}

let THEME = createTheme({
  breakpoints: {
    unit: "px",
    values: {
      xs: 0,
      sm: 575,
      md: 768,
      lg: 1000,
      xl: 1440,
    },
  },
  components: {
    MuiAccordion: ACCORDION_THEME,
    MuiAccordionDetails: ACCORDION_DETAILS_THEME,
    MuiAccordionSummary: ACCORDION_SUMMARY_THEME,
    MuiAlert: ALERT_THEME,
    MuiAlertTitle: ALERT_TITLE_THEME,
    MuiButton: BUTTON_THEME,
    MuiButtonGroup: BUTTON_GROUP_THEME,
    MuiCheckbox: CHECKBOX_THEME,
    MuiChip: CHIP_THEME,
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontFamily: ["Ginto Normal", "sans-serif"].join(","),
        },
      },
    },
    MuiDataGrid: DATA_GRID_PRO_THEME,
    MuiDateRangePickerDay: DATE_RANGE_PICKER_DAY_THEME,
    MuiIconButton: ICON_BUTTON_THEME,
    MuiLinearProgress: PROGRESS_BAR_THEME,
    MuiMenu: MENU_THEME,
    MuiMenuItem: MENU_ITEM_THEME,
    MuiMobileStepper: STEPPER_THEME,
    MuiModal: MODAL_THEME,
    MuiMultiSectionDigitalClockSection:
      MULTI_SECTION_DIGITAL_CLOCK_SECTION_THEME,
    MuiOutlinedInput: OUTLINED_INPUT_THEME,
    MuiPagination: PAGINATION_THEME,
    MuiPickersArrowSwitcher: ARROW_SWITCHER_THEME,
    MuiPickersDay: PICKERS_DAY_THEME,
    MuiPopper: POPPER_THEME,
    MuiRadio: RADIO_THEME,
    MuiSkeleton: SKELETON_THEME,
    MuiSlider: SLIDER_THEME,
    MuiSnackbar: SNACKBAR_THEME,
    MuiSnackbarContent: SNACKBAR_CONTENT_THEME,
    MuiSvgIcon: ICON_THEME,
    MuiSwitch: SWITCH_THEME,
    MuiTab: TAB_THEME,
    MuiTabPanel: TAB_PANEL_THEME,
    MuiTextField: TEXT_FIELD_THEME,
    MuiTooltip: TOOLTIP_THEME,
    MuiTypography: TYPOGRAPHY_THEME,
  },
  palette: {
    common: {
      black: gray[950],
      white: gray[0],
    },
    primary: {
      light: peacock[300],
      main: peacock[700],
      dark: peacock[800],
      contrastText: gray[0],
      ...peacock,
    },
    secondary: {
      light: bubblegum[200],
      main: bubblegum[400],
      dark: bubblegum[700],
      contrastText: gray[0],
      ...bubblegum,
    },
    error: {
      light: redhot[200],
      main: redhot[500],
      dark: redhot[700],
      contrastText: gray[0],
      ...redhot,
    },
    warning: {
      light: sunshine[200],
      main: sunshine[500],
      dark: sunshine[700],
      contrastText: gray[0],
      ...sunshine,
    },
    info: {
      light: waterslide[200],
      main: waterslide[500],
      dark: waterslide[700],
      contrastText: gray[0],
      ...waterslide,
    },
    success: {
      light: apple[200],
      main: apple[500],
      dark: apple[700],
      contrastText: gray[0],
      ...apple,
    },
    grey: gray,
    neutral: {
      light: gray[200],
      main: gray[500],
      dark: gray[800],
      contrastText: gray[0],
      ...gray,
    },
    text: {
      primary: gray[800],
      secondary: gray[600],
      disabled: gray[400],
      default: gray[900],
      subdue: gray[600],
      invert: gray[0],
      brandPrimary: peacock[800],
      brandSecondary: bubblegum[700],
      error: redhot[700],
      warning: sunshine[800],
      info: waterslide[700],
      success: apple[700],
    },
    icon: {
      default: gray[900],
      subdue: gray[600],
      disabled: gray[500],
      invert: gray[0],
      primary: peacock[800],
      secondary: bubblegum[700],
      error: redhot[700],
      warning: sunshine[800],
      info: waterslide[700],
      success: apple[700],
    },
    border: {
      neutralDefault: gray[300],
      neutralLighter: gray[200],
      neutralDarker: gray[400],
      neutralEmphasis: gray[700],
      peacockDefault: peacock[400],
      peacockLighter: peacock[200],
      peacockDarker: peacock[700],
      peacockEmphasis: peacock[800],
    },
    surface: {
      white: gray[0],
      neutral50: gray[50],
      neutral100: gray[100],
      neutral200: gray[200],
      peacock50: peacock[50],
      peacock100: peacock[100],
      peacock200: peacock[200],
      disabled: gray[300],
    },
    overlays: {
      transparentGrayLight: "rgba(46, 46, 46, 0.04)",
      transparentGrayDark: "rgba(46, 46, 46, 0.12)",
    },
  },
  shape: {
    // borderRadius values get multiplied by this value (e.g. borderRadius: 4 -> borderRadius: 4 * theme.shape.borderRadius)
    borderRadius: 1,
  },
  shadows: new Array(25).fill("none") as any,
  spacing: pxToRem,
  typography: {
    fontFamily: ["Ginto Normal", "sans-serif"].join(","),
    // Match htmlFontSize to what we use on website
    htmlFontSize: 10,
    button: {
      fontFamily: "Ginto Normal",
      fontWeight: 500, // medium
      textTransform: "none", // reset base MUI style
    },
  },
});

// Define custom component theming after the theme is created
// styled() uses withTheme internally and will create a circular dependency
if (THEME.components) {
  THEME.components.MuiImage = {
    // Default Image component
    BaseComponent: BaseImage,
  };
}

// Define gradients after the theme is created so we can use the theme.palette values
THEME.gradients = {
  primarySecondary: `linear-gradient(118deg, ${THEME.palette
    .primary[50]!} 31.38%, ${THEME.palette.secondary[50]!} 162.62%)`,
};

// Define box shadows for consistent elevation across components
THEME.boxShadows = shadows;

THEME = applyAdditionalTypographyTheme(THEME);

export default THEME;
